import classNames from 'classnames';
import React, { useCallback, useLayoutEffect, useRef } from 'react';

import styles from './switch-v2.module.css';
import Container from '@ui/container/container';

interface SwitchV2Props<T> {
  value: T;
  on: boolean;
  onClick: (value: T, on: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
  stopPropagation?: boolean;
  paused?: boolean;
  onLabel?: string;
  offLabel?: string;
  fullWidth?: boolean;
}

const SwitchV2 = <T,>({
  value,
  on,
  onClick,
  disabled = false,
  loading = false,
  stopPropagation = false,
  paused = false,
  onLabel = 'On',
  offLabel = 'Off',
  fullWidth = false,
}: SwitchV2Props<T>) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const offLabelRef = useRef<HTMLDivElement>(null);
  const onLabelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (buttonRef.current && offLabelRef.current && onLabelRef.current) {
      if (fullWidth) {
        buttonRef.current.style.setProperty('--switch-width', `100%`);
        buttonRef.current.style.setProperty('--thumb-width-on', `50%`);
        buttonRef.current.style.setProperty('--thumb-width-off', `50%`);
      } else {
        const thumbWidthOn = onLabelRef.current.offsetWidth + 32;
        const thumbWidthOff = offLabelRef.current.offsetWidth + 32;

        const totalWidth = thumbWidthOn + thumbWidthOff;

        buttonRef.current.style.setProperty('--switch-width', `${totalWidth}px`);
        buttonRef.current.style.setProperty('--thumb-width-on', `${thumbWidthOn}px`);
        buttonRef.current.style.setProperty('--thumb-width-off', `${thumbWidthOff}px`);
      }
    }
  }, [onLabel, offLabel, fullWidth]);

  const memoizedClick = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      if (disabled) return;
      if (stopPropagation) {
        e?.stopPropagation();
      }
      onClick(value, !on);
    },
    [disabled, on, onClick, stopPropagation, value]
  );

  return (
    <Container loading={loading} className="flex" borderRadius="100px" width="100%">
      <button
        ref={buttonRef}
        onClick={memoizedClick}
        className={classNames('clear-button', styles['switch-v2'], {
          [styles['on']]: on,
          [styles['isDisabled']]: disabled,
          [styles['paused']]: paused,
          [styles['fullWidth']]: fullWidth,
        })}
        disabled={paused || disabled || loading}
      >
        <div className={styles['thumb']} />
        <div className={styles['gutter']}>
          <div
            className={classNames('flex ai-center height-32 gap-32', {
              'jc-space-around': fullWidth,
              'jc-space-evenly': !fullWidth,
              'flex-1': fullWidth,
            })}
          >
            <div ref={offLabelRef} className="S3 z-10">
              {offLabel}
            </div>
            <div ref={onLabelRef} className="S3 z-10">
              {onLabel}
            </div>
          </div>
        </div>
      </button>
    </Container>
  );
};

export default SwitchV2;
